import React from "react";
import { useDebounce } from "common/effects";
import { compose, withAppContext, withActions, withState } from "containers/HOC";
import { Link } from "react-router-dom";
import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import * as FormUtils from "common/FormUtils";
import Modal from "components/Modal/Modal";
import ModalBody from "components/Modal/ModalBody";
import ModalFooter from "components/Modal/ModalFooter";
import ModalFooterLeft from "components/Modal/ModalFooterLeft";
import ModalFooterRight from "components/Modal/ModalFooterRight";
import Button from "components/Button/Button";
import { Panel, PanelBody } from "components/Panel";
import { Grid, GridCell } from "components/Grid";
import TextField from "components/FormFields/TextField";
import * as utils from "common/utils";
import TargetFarmList from "./TargetFarmList";
import { searchFarms, mergeFarms } from "containers/Admin/_actions";

const MergeFarmsModal = ({ farm, farms, close, searchFarms, mergeFarms, refData }) => {
    const [searchText, setSearchText] = React.useState("");
    const [searchResults, setSearchResults] = React.useState(farms);

    const debouncedSearchText = useDebounce(searchText, 500);

    React.useEffect(() => {
        if (debouncedSearchText) {
            searchFarms(debouncedSearchText)
                .then((results) => {
                    if (results) {
                        setSearchResults(results.filter((r) => r.id !== farm.id));
                    }
                })
                .catch((error) => console.log(error));
        }
    }, [farm, debouncedSearchText, searchFarms]);

    const validate = (viewModel) => {
        const validation = {};
        validation.destinationFarmId = FormUtils.validators.required(viewModel.destinationFarmId);
        return validation;
    };

    const submit = async (viewModel) => {
        const submitResults = await mergeFarms(viewModel.id, viewModel.destinationFarmId)
            .then(close)
            .catch((error) => ({ [FORM_ERROR]: error }));
        return submitResults;
    };

    return (
        <Form initialValues={farm} validate={validate} onSubmit={submit}>
            {({ form, handleSubmit, submitting, submitError, dirtySinceLastSubmit }) => {
                const error = !dirtySinceLastSubmit && submitError;
                const region = farm.region ? utils.valueToText(refData.regions, farm.region) : "-";

                return (
                    <form onSubmit={handleSubmit}>
                        <Modal title="Merge farms" close={close} submitting={submitting}>
                            <ModalBody error={error}>
                                <Panel title="Source farm" skyBlue className="u-mt-lg" notCollapsible>
                                    <PanelBody>
                                        <Grid>
                                            <GridCell>
                                                <Field name="id" type="hidden" component="input" />
                                                <h4 className="u-textWarning">WARNING - THIS FARM WILL BE DELETED!</h4>
                                                <ul className="disc">
                                                    <li>This farm's analyses will be transfered to the target farm. Any duplicate year end analyses will be transfered as predictive analyses.</li>
                                                    <li>This farm will be deleted after it has been merged into the target farm</li>
                                                </ul>
                                                <div className="Table u-mt-md">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Name</th>
                                                                <th>Address</th>
                                                                <th>Region</th>
                                                                <th>Subscription</th>
                                                                <th>Created</th>
                                                                <th>Modified</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td>
                                                                    <Link to={`/app/farm/${farm.id}`} target="_blank">
                                                                        {farm.name}
                                                                    </Link>
                                                                </td>
                                                                <td>{farm.address}</td>
                                                                <td>{region}</td>
                                                                <td className={farm.expired ? "u-textError" : ""}>{farm.expired ? "Expired" : utils.dateLong(farm.expirationDate, "-")}</td>
                                                                <td>{utils.dateLong(farm.createdOn, "-")}</td>
                                                                <td>{utils.dateLong(farm.modifiedOn, "-")}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </GridCell>
                                        </Grid>
                                    </PanelBody>
                                </Panel>
                                <Panel title="Destination farm" green notCollapsible>
                                    <PanelBody>
                                        <Grid>
                                            <GridCell>
                                                <Field name="searchText" placeholder="Enter search text" onChange={setSearchText} className="u-mt-md" autoFocus component={TextField} />
                                            </GridCell>
                                            <GridCell className="u-mt-lg">
                                                <TargetFarmList form={form} farms={searchResults} refData={refData} />
                                            </GridCell>
                                        </Grid>
                                    </PanelBody>
                                </Panel>
                            </ModalBody>
                            <ModalFooter>
                                <ModalFooterLeft>
                                    <Button id="cancel" onClick={close} secondary disabled={submitting}>
                                        Cancel
                                    </Button>
                                </ModalFooterLeft>
                                <ModalFooterRight>
                                    <Button id="submit" submit primary disabled={submitting}>
                                        Merge
                                    </Button>
                                </ModalFooterRight>
                            </ModalFooter>
                        </Modal>
                    </form>
                );
            }}
        </Form>
    );
};

export default compose(
    withAppContext,
    withActions({ searchFarms, mergeFarms }),
    withState((state, ownProps) => {
        return {
            farms: (state.admin.farms || []).filter((f) => f.id !== ownProps.farm.id),
        };
    })
)(MergeFarmsModal);
